import { AudioAssessmentResult } from '@/types/pronouciation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SentenceTableProps {
    sentences: AudioAssessmentResult['characters'][];
}

export const SentenceTable: React.FC<SentenceTableProps> = ({ sentences }) => {
    const { t } = useTranslation();
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const toggleRow = useCallback(
        (index: number) => {
            const newExpanded = new Set(expandedRows);
            if (newExpanded.has(index)) {
                newExpanded.delete(index);
            } else {
                newExpanded.add(index);
            }
            setExpandedRows(newExpanded);
        },
        [expandedRows],
    );

    const getSentenceText = useCallback((sentence: AudioAssessmentResult['characters']): string => {
        return sentence.map((word) => word.text).join('');
    }, []);

    const getSentenceAverageScore = useCallback((sentence: AudioAssessmentResult['characters']): number => {
        if (sentence.length === 0) return 0;
        const totalScore = sentence.reduce((sum, word) => sum + word.score, 0);
        return Math.round(totalScore / sentence.length);
    }, []);

    if (!sentences || sentences.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color='text.secondary'>{t('assessment.audio.label.sentencesEmpty')}</Typography>
            </Box>
        );
    }

    return (
        <TableContainer
            component={Paper}
            sx={{
                border: '1px solid',
                borderColor: 'grey.200',
            }}
        >
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>{t('assessment.audio.sentences')}</TableCell>
                        <TableCell align='center' sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                            {t('assessment.audio.overall')}
                        </TableCell>
                        <TableCell align='center' sx={{ fontWeight: 'bold', backgroundColor: 'grey.50', width: 60 }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sentences.map((sentence, index) => {
                        const isExpanded = expandedRows.has(index);
                        const averageScore = getSentenceAverageScore(sentence);
                        const sentenceText = getSentenceText(sentence);

                        return (
                            <React.Fragment key={index}>
                                {/* Main sentence row */}
                                <TableRow
                                    sx={{
                                        '&:nth-of-type(odd)': { backgroundColor: 'grey.25' },
                                        '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 'medium', fontSize: '1.1rem' }}>{sentenceText}</TableCell>
                                    <TableCell align='center'>
                                        <Typography variant='body2' sx={{ minWidth: 50, fontWeight: 'bold' }}>
                                            {averageScore}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <IconButton
                                            size='small'
                                            onClick={() => toggleRow(index)}
                                            sx={{
                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s',
                                            }}
                                        >
                                            <ExpandMoreIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>

                                {/* Expanded word details row */}
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={isExpanded} timeout='auto'>
                                            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                                <Table size='small'>
                                                    <TableBody>
                                                        {sentence.map((word, wordIndex) => (
                                                            <TableRow key={wordIndex}>
                                                                <TableCell sx={{ fontWeight: 'medium', fontSize: '1rem' }}>{word.text}</TableCell>
                                                                <TableCell align='center'>
                                                                    <Typography
                                                                        variant='body2'
                                                                        sx={{
                                                                            fontWeight: 'medium',
                                                                        }}
                                                                    >
                                                                        {word.score.toFixed(2)}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
